import { DatePipe, DecimalPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, OnInit, output } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { projectNameInvalidCharValidator, projectNameValidator, } from '@app/cem/project/content/project-create-new/project-create-new.component';
import { GeneralProjectService } from '@app/cem/project/services/general-project.service';
import { ProjectObserverService } from '@app/cem/project/services/project-observer.service';
import { AppProjectChangeNotificationService } from '@app/common/services/app-signal.service';
import { DataLookupService } from '@app/common/services/data-api/data-lookup.service';
import { PlexEnvironment } from '@app/env';
import { ButtonLoadingDirective } from '@app/shared/directives/button-loading/button-loading.directive';
import { DisabledControlDirective } from '@app/shared/directives/disabled-control/disabled-control.directive';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { nameof } from '@app/shared/utilities/javascript-utilities';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { NumericTextBoxComponent, TextAreaComponent, TextBoxComponent } from '@progress/kendo-angular-inputs';
import { NumberPipe } from '@progress/kendo-angular-intl';
import { ProjectBaseScenarioTabItemComponent } from 'app/cem/project/content/project-base-scenario-tabitem.component';
import { ProjectCreateUrls } from 'app/cem/project/content/project-create-new/project-create.urls';
import { DataDescriptor } from 'app/cem/project/services/data-descriptor.model';
import { ProjectScreenBehaviorService } from 'app/cem/project/services/project-screenbehavior.service';
import { ColumnMap } from 'app/common/services/data-api/column-map';
import { ErrorService } from 'app/common/services/error.service';
import { ProjectEstimateRecordType, ProjectEstimateStatus, ProjectStatusId, ProjectSubTypeId, ProjectTypeId } from 'app/shared/data/project-enums';
import { IProject } from 'app/shared/interfaces';
import { MajorMilestoneProjectTypeVisibilityScheduleView, ProjectEstimate, ProjectTypeScreenBehavior, Schedule, ScheduleTemplateName, Table, } from 'app/shared/models';
import { NameValueInt, NameValueIntParentInt, NameValueString } from 'app/shared/models/name-value';
import { ProjectEACAndBudgetAFEModel } from 'app/shared/models/project-eac-budget-afe';
import { zip } from 'rxjs';
import { Project } from '../../../../shared/models/project';
import { ProjectStatus } from '../../../../shared/models/project-status';
import { RecordStatus } from '../../enums/record-status';
import { ProjectStatusMatrix } from '../../models/project-status-matrix';
import { ProjectSubType } from '../../models/project-sub-type';
import { AfeDataSharingService } from '../../services/afe-data-sharing.service';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';
import { ProjectEstimateService } from '../../services/project-estimate.service';
import { ProjectHeaderRefreshService } from '../../services/project-header-data-refresh';
import { ProjectHeaderService } from '../../services/project-header.service';
import { BatteryDesignType, ProjectApprovalMatrix } from '../project-create-new/project-create.models';
import { IProjectStatusDto } from './project-status-dto';

@Component({
  selector: 'app-project-header-data',
  templateUrl: './project-header-data.component.html',
  styleUrls: ['./project-header-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    ButtonLoadingDirective,
    ComboBoxComponent,
    DatePipe,
    DecimalPipe,
    DisabledControlDirective,
    FormsModule,
    LoaderComponent,
    NumberPipe,
    NumericTextBoxComponent,
    ReactiveFormsModule,
    TextAreaComponent,
    TextBoxComponent,
  ],
})
export class ProjectHeaderDataComponent extends ProjectBaseScenarioTabItemComponent implements OnInit, AfterViewInit {
  public screenBehaviors = inject(ProjectScreenBehaviorService);
  private fb = inject(UntypedFormBuilder);
  private lookupPlusService = inject(LookupPlusService);
  private projectHeaderService = inject(ProjectHeaderService);
  private lookupService = inject(DataLookupService);
  private errorService = inject(ErrorService);
  private urls = inject(ProjectCreateUrls);
  private _elRef = inject(ElementRef);
  private projectEstimateService = inject(ProjectEstimateService);
  private generalProjectService = inject(GeneralProjectService);
  private projectChangeNotify = inject(AppProjectChangeNotificationService);
  public environment = inject(PlexEnvironment);
  private projectHeaderData = inject(ProjectHeaderRefreshService);
  private afeDataSharingSerivce = inject(AfeDataSharingService);
  private poSvc = inject(ProjectObserverService);

  readonly tabTitle = output<string>();
  readonly tabTdv = output<boolean>();
  readonly headerStatus = output<HeaderStatusOutput>();    //ASYNC

  public icons = kendoIcons;
  public projectId: number;
  public defaultProjectTeam: string;
  public isDefaultProjectTeamSync: boolean;
  public debugging: boolean = false;
  public ResettingForm: boolean = false;
  public ProcessingForm: boolean = false;
  public showPdfReportButton: boolean = false;

  private readonly baseNetValueLabel = 'Net Value (WI * WBS Amount)';
  public NetValueLabel = this.baseNetValueLabel; //, WI must be > 0.1%';

  public ignoreProjectChanged: boolean = false;

  public tableDefaultProjectTeam: string = 'DefaultProjectTeams';
  public tableConstructionType: string = 'ConstructionTypes';
  public tableTeamDevelopmentArea: string = 'TeamDevelopmentAreas';
  public dataTypeCurveArea = new Array<NameValueIntParentInt>();
  public tProjectSubType: string = 'ProjectSubTypes';
  public fProjectSubType: string = '$filter=ProjectTypeId%20eq%20';
  public oProjectSubType: string;
  public dProjectSubType = new Array<ProjectSubType>();
  public tProjectStatus: string = 'ProjectStatuses';
  public tProjectStatusMatrix: string = 'ProjectStatusMatrix';
  public dProjectStatus = new Array<ProjectStatus>();
  public dProjectStatusMatrix = new Array<ProjectStatusMatrix>();
  public dComboBoxProjectStatus = new Array<IProjectStatusDto>();
  public dManualValidProjectStatus = new Array<ProjectStatus>();
  public majormilestonevisibilities: MajorMilestoneProjectTypeVisibilityScheduleView[] = [];
  public loadingmajormilestonevisibilities: boolean = true;
  public afeBudget: number;
  public loading: boolean = true;
  public formLoaded: boolean = false;
  public workingInterestSettable: boolean = false;
  public percentFormat: string = 'p5';
  public projectNameValidatorRegex: RegExp = /[\"/\\\\<>,:|*?]/;
  public dProjectAssets = new Array<any>();
  public dWellAssetIdsToCheckForWi = new Array<number>();
  public dWellWorkingInterests = new Array<ProjectAsset>();
  public totalWorkingInterest: number;
  public projectVariance: number;
  public projectPctVariance: number;
  public canCreateWellViewProject: boolean;
  public constructionTypeLabel: string = 'Construction Type';
  public constructionTypeOrSequence = new Array<DataDescriptor>();
  public FilteredExploreDevelopIds = new Array<NameValueInt>();
  public projectEacAndBudgetModel: ProjectEACAndBudgetAFEModel;
  public showingValidationErrors: boolean = false;
  public ValidationErrors: NameValueString[] = [];
  public refreshClicked: boolean = false;

  public AfeApprovalTeamsData = new Array<ProjectApprovalMatrix>();
  public AfeApprovalTeamsFiltered: ProjectApprovalMatrix[] | null = null;
  public approvalTeamUrl: string | null = null;
  public BatteryDesignTypeIds = new Array<BatteryDesignType>();
  public ProjectTypeId = ProjectTypeId

  public dataSources: IHeaderControls | any = {
    ConstructionType: DataDescriptor.namevalue(),
    ConstructionSequence: DataDescriptor.namevalue(),
    DefaultProjectTeam: DataDescriptor.namevalue(),
    StandardConfiguration: DataDescriptor.namevalue(),
    Function: DataDescriptor.namevalue(),
    Division: DataDescriptor.namevalue(),
    ExploreDevelopIds: DataDescriptor.namevalue()
  };

  public fieldVisibility: IHeaderControls = {
    IsNeedByDateSync: true,
    IsNeedByDateOverride: true,
    IsScheduleSync: true,
    DefaultProjectTeam: true,
    StandardConfiguration: true,
    IsProjectTeamSync: true,
    TypeCurveArea: true,
    ConstructionType: true,
    ProjectSubType: true,
    Name: true,
    ProjectDescription: true,
    ProjectEconomics: true,
    Location: true,
    Prospect: true,
    ProjectStatus: true,
    Function: true,
    Division: true,
    ExploreDevelopIds: true,
    ProjectWorkingInterest: true,
    NetValue: true,
    AfeApprovalTeam: true,
    WBSType: true,
    WBSSubType: true,
    WBSNumber: true,
    BatteryDesignTypeIds: true
  };

  public controlsConfig: IHeaderControls = {
    IsNeedByDateSync: [null],
    IsNeedByDateOverride: [null],
    IsScheduleSync: [null],
    DefaultProjectTeam: [null],
    StandardConfiguration: [null],
    IsProjectTeamSync: true,
    TypeCurveArea: [null],
    ConstructionType: [null],
    ProjectSubType: [null],
    Name: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(69),
    projectNameValidator(this.projectNameValidatorRegex), projectNameInvalidCharValidator()]],
    ProjectDescription: ['', [Validators.maxLength(250), projectNameInvalidCharValidator()]],
    Location: ['', [Validators.maxLength(250), projectNameInvalidCharValidator()]],
    Prospect: ['', [Validators.maxLength(250), projectNameInvalidCharValidator()]],
    ProjectStatus: [null],
    Function: [null],
    Division: [null],
    ExploreDevelopIds: [null, Validators.required],
    AfeApprovalTeam: [null, Validators.required],
    ProjectWorkingInterest: [null],
    NetValue: [null],
    ProjectEconomics: ['', [Validators.maxLength(250), projectNameInvalidCharValidator()]],
    WBSType: [{ value: null, disabled: true }],
    WBSSubType: [{ value: null, disabled: true }],
    WBSNumber: [{ value: null, disabled: true }],
    BatteryDesignTypeIds: [null]
  };

  public tableProject: string = 'Projects';
  public dataProject: Project;
  public hasConstructionType: boolean;
  public placeHolderProjectStatus: string = 'Project Status';
  public scheduleTemplate: ScheduleTemplateName;

  public ProjectHeaderForm: UntypedFormGroup;
  public projectHeaderUpdate: IProject;

  // accounting default value - used for initial AFE and Working Interest fields
  public acctDefault: number = 12345.6;
  public accPctDefault: number = .35;

  private oDataProjectQueryA: string = '?%24expand=DefaultProjectTeam%2CProjectType%2CAssetTeam%2COperatingUnit';
  private oDataProjectQueryB: string = '%2CProjectStatus%2CDivision%2CArea%2CFunction%2CConstructionType%2C%20ProjectSubType';
  private oDataProjectQuery: string;

  handleFilter(filter: string, dataSource: any[], filteredSourceName: string, fieldName: string) {
    const contains = value => s => s[fieldName].toLowerCase().indexOf(value.toLowerCase()) !== -1;
    let filteredSource = dataSource.filter(contains(filter));
    this[filteredSourceName] = filteredSource;
  }

  constructor() {
    super();
  }

  async ngOnInit() {
    this.generateForm();
    this.oDataProjectQuery = this.oDataProjectQueryA + this.oDataProjectQueryB;
    this.projectId = this.projectTab().ProjectId;

    let table: string = 'projectheader';
    let query: string = `projectId=${this.projectId}`;
    this.lookupPlusService.getLookupNoCount(table, query)
      .then(x => {
        if (x < 0.00001) {
          this.totalWorkingInterest = null;
        } else {
          this.totalWorkingInterest = x;
        }

      });

    let devAreaMapper: ColumnMap[] = [
      new ColumnMap('name', 'Description'),
      new ColumnMap('value', 'AreaId'),
      new ColumnMap('parentValue', 'AssetTeamId')
    ];

    let startup = zip(
      this.getDescLookupData(Table.ConstructionTypes, this.dataSources.ConstructionType),
      this.getDescLookupData(Table.ConstructionSequences, this.dataSources.ConstructionSequence),
      this.getDescLookupData(Table.Functions, this.dataSources.Function),
      this.getDescLookupData(Table.Divisions, this.dataSources.Division, `&$filter=AssetTeamId eq ${this.projectTab().AssetTeamId} `),
      this.getDescLookupData(Table.ExploreDevelops, this.dataSources.ExploreDevelopIds),
      this.getLookupDataWithParents(Table.Areas, this.dataTypeCurveArea, this.urls.TypeCurveAreaFilter, devAreaMapper),
      this.getProjectStatusMatrix(this.tProjectStatusMatrix),
      this.getEACAndAFEValues()
    );
    startup.subscribe(r => {
      this.getProjectHeaderInformation(this.projectId, this.oDataProjectQuery);
    });

    this.projectChangeNotify.NotifyProjectChange.subscribe(async change => {
      if (!change || change.ProjectId !== this.projectId) return;
      let currentPS = this.ProjectHeaderForm.get('ProjectStatus').value as ProjectStatus;
      if (currentPS && currentPS.ProjectStatusId != change.ProjectStatusId) {
        await this.changeProjectStatus(change.ProjectStatusId, false);
        this.errorService.display(`The Project Status for ${this.projectId} was changed and pushed to you. Change was by ${change.UpdatedBy}.`, null, false)
      }
      //TODO: notify user that change was made
    });

    this.projectTab().ProjectChanged.subscribe(p => {
      if (this.ignoreProjectChanged) {
        return;
      }
      if (p.DefaultProjectTeamId !== this.dataProject.DefaultProjectTeamId) {
        this.dataProject.DefaultProjectTeamId = p.DefaultProjectTeamId;
        if (p.DefaultProjectTeamId >= 1) {
          let team = this.dataSources.DefaultProjectTeam.source.find(tr => tr.value === p.DefaultProjectTeamId);
          if (team) {
            this.defaultProjectTeam = team.name;
          } else {
            this.defaultProjectTeam = '[TEAM NOT FOUND]';
          }
        } else {
          this.defaultProjectTeam = 'Custom';
        }
      }
    });
    this.projectEstimateService.estimateChanged.subscribe((changed) => {
      if (changed) {
        this.setDivisionEnabledDisabledForAfeStatus();
      }
    })
    this.projectHeaderData.refresh.subscribe(s => {
      this.getProjectHeaderInformation(this.projectId, this.oDataProjectQuery);
    })
    this.ProjectHeaderForm.controls.WBSNumber.valueChanges.subscribe((value) => {
      if (value) {
        this.afeDataSharingSerivce.setAfeNumber(value)
      }
    })
  }

  ngAfterViewInit() {
    this.poSvc.projectObserve('header', this.ProjectHeaderForm.valid);
  }

  public handleAfeApprovalTeamSelectChange(approvalMatrix: ProjectApprovalMatrix): void {
    const control = this.ProjectHeaderForm.get('AfeApprovalTeam');
    this.setValidation(this.ProjectHeaderForm.controls.AfeApprovalTeam, !approvalMatrix);

    if (!approvalMatrix) {
      control?.reset();
      this.approvalTeamUrl = null;
    } else {
      control?.setValue(approvalMatrix);
      let projectApproval: ProjectApprovalMatrix = approvalMatrix;
      this.approvalTeamUrl = `${this.environment.executeUrl}ui/view?AFE_APPROVAL_PATH/${projectApproval.DocumentId}`;
    }

    control?.updateValueAndValidity();
  }

  public stdConfigSync() {
    this.projectHeaderService.getProjectHeader(this.projectId, this.oDataProjectQuery)
      .then(async (payload: Project) => {
        let stdConfig = this.dataSources.StandardConfiguration.filtered.find(x => x.value === payload.DefaultStandardConfigurationId);
        let p = {
          StandardConfiguration: stdConfig,
        };
        this.ProjectHeaderForm.patchValue(p);
      });
  }

  public runTabActivity(sync: boolean, defaultTeam: string): void {
    if (defaultTeam) {
      this.defaultProjectTeam = defaultTeam;
    }
    if (sync !== null && sync !== undefined) {
      this.ProjectHeaderForm.patchValue(
        {
          IsProjectTeamSync: sync
        }
      );
    }
    this.ProjectHeaderForm.controls.IsProjectTeamSync.disable();
  }

  public async getProjectStatusMatrix(table: string): Promise<any> {
    return await this.lookupPlusService.getLookupNoCount(table)
      .then(async (payload) => {
        this.dProjectStatusMatrix = payload.value;
        await this.getProjectStatuses(this.tProjectStatus);
        return payload;
      }
      );
  }

  public async getAfeProjectTeams(assetTeamId: number, projectTypeId: number, projectSubTypeId: number, exploreDevelopId: number): Promise<ProjectApprovalMatrix[]> {

    try {
      if (!projectTypeId || !exploreDevelopId) return [];
      let url = `ProjectApprovalMatrix?$filter=AssetTeamId eq ${assetTeamId} and ProjectTypeId eq ${projectTypeId}`;
      if (projectSubTypeId) {
        url += ` and (ProjectSubTypeId eq ${projectSubTypeId} or ProjectSubTypeId eq null)`;
      }
      if (exploreDevelopId) {
        url += ` and (ExploreDevelopId eq ${exploreDevelopId} or ExploreDevelopId eq null) `;
      }
      const payload = await this.lookupPlusService.getLookupCustom(url);
      return payload.value;
    } catch (error) {
      this.errorService.error("Failed to fetch AFE project teams API:", error);
      return [];
    }
  }

  public async getBatteryDesignTypes(): Promise<BatteryDesignType[]> {
    try {
      const payload = await this.lookupPlusService.getLookupCustom('BatteryDesignTypes?$select=BatteryDesignTypeId,Description');
      return payload.value;
    } catch (error) {
      this.errorService.error("Failed to fetch Battery Design Types API:", error);
      return [];
    }
  }

  public async getProjectWBSType(projectTypeId: number, projectSubTypeId: number, exploreDevelopId: number) {
    try {
      if (!projectTypeId) return;
      let url = `WBStoPlexProjectTypesMapping?$filter=ProjectType eq ${projectTypeId}`
      if (projectSubTypeId) {
        url += ` and (ProjectSubType eq ${projectSubTypeId} or ProjectSubType eq null)`;
      }
      else {
        url += ` and (ProjectSubType eq null)`;
      }
      if (exploreDevelopId) {
        url += ` and (ExploreDevelop eq ${exploreDevelopId} or ExploreDevelop eq null) `;
      }
      const payload = await this.lookupPlusService.getLookupCustom(url);
      return payload.value;
    }
    catch (error) {
      this.errorService.error("Failed to fetch WBS project types", error);
      return [];
    }
  }

  public async getEACAndAFEValues(): Promise<ProjectEACAndBudgetAFEModel> {
    return this.lookupPlusService.getTypedObject<ProjectEACAndBudgetAFEModel>('projectdata', `getEACAndAFEFieldValues/${this.projectId}`)
      .then(payload => {
        this.projectEacAndBudgetModel = payload;
        return payload;
      });
  }

  public async getScheduleTemplateName(): Promise<ScheduleTemplateName> {
    let scheduleId = this.dataProject.ScheduleId;
    if (!this.scheduleTemplate && scheduleId) {
      let filter = `$expand=ScheduleTemplateName&$filter=ScheduleId%20eq%20${scheduleId}&$select=ScheduleTemplateName`;
      return this.lookupPlusService
        .getTypedLookup<Schedule>('Schedules', filter)
        .then(stn => {
          if (stn && stn.data && stn.data.length) {
            this.scheduleTemplate = stn.data[0].ScheduleTemplateName;
            return this.scheduleTemplate;
          }
        }
        )
        .catch(e => {
          this.errorService.error('Error Getting Schedule Schedule Template.', e);
          return null;
        });
    } else {
      return Promise.resolve(this.scheduleTemplate);
    }
  }

  public async getMajorMilestoneVisibilities(): Promise<any[]> {
    let scheduleId = this.dataProject.ScheduleId;
    if (!this.majormilestonevisibilities.length && scheduleId) {
      let filter = `$filter=ScheduleId eq ${scheduleId}`;
      return this.lookupPlusService
        .getTypedLookup<MajorMilestoneProjectTypeVisibilityScheduleView>('MajorMilestoneProjectTypeVisibilityScheduleView', filter)
        .then(mm => {
          this.loadingmajormilestonevisibilities = false;
          this.majormilestonevisibilities = mm.data;
          return mm.data;
        }
        )
        .catch(e => {
          this.loadingmajormilestonevisibilities = false;
          this.errorService.error('Error Getting Major Milestone Top List.', e);
          return [];
        });
    } else {
      this.loadingmajormilestonevisibilities = false;
      return Promise.resolve(this.majormilestonevisibilities);
    }
  }

  public async getProjectStatusMatrixByCurrentStatus(table: string, status: ProjectStatus): Promise<any> {
    this.dComboBoxProjectStatus = [];
    let xUrl: string = `projectdata/getManualValidProjectStatus/${this.projectId}`;
    return await this.projectEstimateService.postNoPayload(xUrl)
      .then(payload => {
        let alreadyIn = false;
        let z = payload.ProjectStatusData;
        payload.ProjectStatusData.forEach(element => {
          this.dComboBoxProjectStatus.push(element);
          if (status && element.ProjectStatusId == status.ProjectStatusId) {
            alreadyIn = true;
          }
        });
        this.dProjectStatusMatrix = payload.value;
        if (status) {
          if (!alreadyIn) {
            let currentStatus = { ProjectStatusId: status.ProjectStatusId, Description: status.Description, Enabled: true, SortOrder: 0 };
            this.dComboBoxProjectStatus.unshift(currentStatus);
          }
          this.placeHolderProjectStatus = status.Description;
        }
      });
  }

  public async getProjectStatuses(table: string) {
    return await this.lookupPlusService.getLookupNoCount(table, null, true)
      .then(payload => {
        this.dProjectStatus = payload.value;
      });
  }

  public getLookupDataWithParents(table: Table,
    data: NameValueIntParentInt[],
    query: string = null,
    lookupMap: ColumnMap[],
  ): Promise<NameValueInt[]> {

    if (data.length) {
      let obx = Promise.resolve<NameValueInt[]>(data);
      return obx;
    }

    let ob = this.lookupService.get(table, lookupMap, true, false, query);

    ob.then(payload => {
      payload = payload.sort((a, b) => this.sortByName(a, b));
      this.assignLookupData(payload, data);
    });
    return ob;
  }

  public assignLookupData(payload: NameValueInt[], dest: NameValueInt[] | DataDescriptor) {
    if (!payload) return;
    let dd = ((dest as DataDescriptor).source) || (dest as NameValueInt[]);
    let ddF = (dest as DataDescriptor).filtered;
    for (let i = 0; i < payload.length; i++) {
      let p = payload[i];
      if (p) {
        dd.push(p);
        if (ddF) {
          ddF.push(p);
        }
      }
    }
  }

  public assignDescLookupData(payload: NameValueInt[], dest: DataDescriptor) {
    if (!payload) return;
    for (let i = 0; i < payload.length; i++) {
      let p = payload[i];
      if (p) {
        dest.source.push(p);
        dest.filtered.push(p);
      }
    }
  }

  public sortByName(a: { name: string }, b: { name: string }) {
    const genreA = a.name && a.name.toUpperCase();
    const genreB = b.name && b.name.toUpperCase();
    let comparison = 0;
    if (genreA > genreB) {
      comparison = 1;
    } else if (genreA < genreB) {
      comparison = -1;
    }
    return comparison;
  }

  public getLookupData(table: string | Table, data: NameValueInt[] | DataDescriptor, query: string = null): Promise<NameValueInt[]> {
    if ((data as NameValueInt[]).length) {
      let obx = Promise.resolve<NameValueInt[]>(data as NameValueInt[]);
      return obx;
    }
    else if ((data as DataDescriptor).source && (data as DataDescriptor).source.length) {
      let x = data as DataDescriptor;
      let obx = Promise.resolve<NameValueInt[]>(x.source);
      return obx;
    }

    let ob = this.lookupService.getNameValue(table, query);
    ob.then(payload => {

      payload = payload.sort((a, b) => this.sortByName(a, b));
      this.assignLookupData(payload, data);
    });
    return ob;
  }

  public getDescLookupData(table: string | Table, data: DataDescriptor, query: string = null): Promise<NameValueInt[]> {
    if (data && data.source && data.source.length) {
      let obx = Promise.resolve<NameValueInt[]>(data.source);
      return obx;
    }
    let ob = this.lookupService.getNameValue(table, query, null, true);
    ob.then(payload => {
      payload = payload.sort((a, b) => this.sortByName(a, b));
      this.assignDescLookupData(payload, data);
    });
    return ob;
  }

  public async getProjectSubTypes(table: string, o: string): Promise<any[]> {
    if (this.dProjectSubType && this.dProjectSubType.length) {
      return Promise.resolve(this.dProjectSubType);
    }
    return this.lookupPlusService.getLookup(table, o)
      .then(payload => {
        this.dProjectSubType = payload.value;
        return payload.value;
      });
  }

  public controlName(param: (type: IHeaderControls) => string): string {
    return nameof<IHeaderControls>(param);
  }

  private ResetForm(): void {
    this.ResettingForm = true;
    this.ProjectHeaderForm.reset();
    this.ResettingForm = false;
  }

  public async onSubmit(e) {
    if (this.ProjectHeaderForm.invalid) {
      return;
    }
    this.validateAfeApprovalTeam();
    let existingProjectName = this.dataProject.Name;
    this.projectHeaderUpdate = this.prepareSave();
    await this.projectHeaderService.update(this.projectId, this.projectHeaderUpdate);
    await this.getProjectHeaderInformation(this.projectId, this.oDataProjectQuery);
    this.ResetForm();
    this.clearTDV();
    this.projectTab().Project = this.dataProject;
    this.ignoreProjectChanged = true;
    this.projectTab().ProjectChanged.next(this.projectTab().Project);
    this.ignoreProjectChanged = false;
    // Trigger new attachment (latest approved AFE/revision record) with project name change
    this.validateAndCreatePdf(existingProjectName);
  }

  public showValidationErrors() {
    this.ValidationErrors = this.getFormValidationErrors(this.ProjectHeaderForm);
    this.showingValidationErrors = true;
  }

  public refreshAFEApprovalTeams(useNewEngine: boolean) {
    try {
      if (this.projectTab().ProjectTypeId !== undefined) {
        this.refreshAfeApprovalProjectTeamList();
      }
    }
    catch (error) {
      this.errorService.error("Failed to refresh AFE project teams:", error);
    }
  }

  public async getProjectHeaderInformation(_fz: number, o: string) {
    await this.projectHeaderService.getProjectHeader(_fz, o)
      .then(async (payload: Project) => {
        await this.processProjectHeaderInformation(payload);
        this.loading = false;
        this.ProjectHeaderForm.controls.IsProjectTeamSync.disable();
        this.setDivisionEnabledDisabledForAfeStatus();
      });
  }

  private setValidation(control: AbstractControl, required: boolean) {
    if (required) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity();
  }

  private setDefaultAfeApprovalProjectTeamList(assetTeamId: number, projectTypeId: number, projectSubTypeId: number, selected: string, exploreDevelopId: number) {
    this.getAfeProjectTeams(assetTeamId, projectTypeId, projectSubTypeId, exploreDevelopId)
      .then(resp => {
        this.AfeApprovalTeamsData = resp;
        this.AfeApprovalTeamsFiltered = resp;
        if (this.AfeApprovalTeamsData.length > 0) {
          this.ProjectHeaderForm.controls.AfeApprovalTeam.enable();
          this.ProjectHeaderForm.controls.AfeApprovalTeam.setValue(selected);
          let selectedapprovalTeam = this.AfeApprovalTeamsData.find(val => val.ProjectTeam === selected);
          this.handleAfeApprovalTeamSelectChange(selectedapprovalTeam);
        } else {
          this.ProjectHeaderForm.controls.AfeApprovalTeam.disable();
        }
      })
      .catch(error => {
        this.errorService.error("Error resolving promise:", error);
      });
  }

  private setProjectWBSType(projecttypeId: number, projectSubTypeId: number, exploreDevelop: number) {
    this.getProjectWBSType(projecttypeId, projectSubTypeId, exploreDevelop).then(resp => {
      if (resp.length > 0) {
        this.ProjectHeaderForm.controls.WBSType.setValue(resp[0].WBSType);
        this.ProjectHeaderForm.controls.WBSSubType.setValue(resp[0].WBSSubType);
      }

      this.ProjectHeaderForm.controls.WBSType.disable();
      this.ProjectHeaderForm.controls.WBSSubType.disable();
    })
  }

  private refreshAfeApprovalProjectTeamList(): void {
    this.refreshClicked = true;
    const formModel: IHeaderControls = this.ProjectHeaderForm.value;
    this.generalProjectService.refreshProjectApprovalMatrix()
      .subscribe({
        next: (result) => {
          this.setDefaultAfeApprovalProjectTeamList(this.projectTab().AssetTeamId, this.projectTab().ProjectTypeId, this.projectTab().ProjectSubTypeId, formModel.AfeApprovalTeam?.projectName, formModel.ExploreDevelopIds?.value);
          this.refreshClicked = false;
        },
        error: (err) => {
          this.refreshClicked = false;
          this.errorService.error('Error refreshing matrix:', err);
        }
      });
  }

  public async setDivisionEnabledDisabledForAfeStatus() {
    this.ProjectHeaderForm.controls.Division.enable();

    let approvedOrPendingAfeCount = await this.projectEstimateService.getDraftOrRejectedEstimateCount(this.projectId, RecordStatus.Rejected, ProjectEstimateStatus.Draft);

    if (approvedOrPendingAfeCount <= 0) {
      this.ProjectHeaderForm.controls.Division.disable();
    }
  }

  private handleExploreDevelopIds(ptId: number) {
    this.FilteredExploreDevelopIds = this.dataSources.ExploreDevelopIds.filtered.filter(x => x.value != 3);
    this.ProjectHeaderForm.controls.ExploreDevelopIds.enable();
  }

  public async validateAndCreatePdf(existingProjectName: string) {
    let updatedProjectName: string = this.ProjectHeaderForm.value.Name;
    if (existingProjectName === updatedProjectName) {
      return;
    }
    const projectTab = this.projectTab();
    if (projectTab.Project.ProjectStatusId === ProjectStatusId.Closed || projectTab.Project.ProjectStatusId === ProjectStatusId.Cancelled) {
      return;
    }

    let projectEstimate = await this.getEstimate();
  }

  public async validateAfeApprovalTeam() {
    const control = this.ProjectHeaderForm.controls.AfeApprovalTeam?.value;
    const isNullOrUndefined = control === undefined || control === null;
    this.setValidation(this.ProjectHeaderForm.controls.AfeApprovalTeam, isNullOrUndefined);
  }

  public async getEstimate() {
    let estimate: ProjectEstimate = null;
    const payload = await this.projectEstimateService.getEstimatesByProjectId(this.projectId);
    estimate = payload.find(x => x.ProjectEstimateStatusId === ProjectEstimateStatus.LatestPublished && x.RecordStatus === RecordStatus.Approved && x.RecordTypeId === ProjectEstimateRecordType.AfeRevision);
    if (estimate === undefined || estimate === null) {
      estimate = payload.find(x => x.ProjectEstimateStatusId === ProjectEstimateStatus.LatestPublished && x.RecordStatus === RecordStatus.Approved && x.RecordTypeId === ProjectEstimateRecordType.Afe);
    }
    return estimate;
  }

  public prepareSave() {
    const formModel: IHeaderControls = this.ProjectHeaderForm.value;
    let saveHeader: IProject | any = {
      IsNeedByDateSync: formModel.IsNeedByDateSync,
      IsNeedByDateOverride: formModel.IsNeedByDateOverride,
      IsScheduleSync: formModel.IsScheduleSync,
      ProjectDescription: formModel.ProjectDescription,
      ProjectEconomics: formModel.ProjectEconomics,
      Location: formModel.Location,
      Prospect: formModel.Prospect,
      ProjectStatusId: formModel.ProjectStatus && formModel.ProjectStatus.ProjectStatusId,
      AfeApprovalTeam: formModel.AfeApprovalTeam ? formModel.AfeApprovalTeam.ProjectTeam : null,
      ExploreDevelopId: formModel.ExploreDevelopIds ? formModel.ExploreDevelopIds.value : this.dataProject.ExploreDevelopId
    };
    // Construction Type May not Exist on this project, so check before adding to patch request
    if (this.fieldVisibility.ConstructionType && formModel.ConstructionType) {
      saveHeader.ConstructionTypeId = formModel.ConstructionType.value;
    }
    if (this.fieldVisibility && formModel.ProjectSubType) {
      saveHeader.ProjectSubTypeId = formModel.ProjectSubType.ProjectSubTypeId;
    }
    if (this.fieldVisibility.Division && formModel.Division) {
      saveHeader.DivisionId = formModel.Division.value;
    }
    if (this.fieldVisibility.Function && formModel.Function) {
      saveHeader.FunctionId = formModel.Function.value;
    }
    if (this.workingInterestSettable && formModel.ProjectWorkingInterest) {
      saveHeader.WorkingInterest = formModel.ProjectWorkingInterest;
    }
    if (formModel.Name) {
      saveHeader.Name = formModel.Name;
    }
    if (formModel.BatteryDesignTypeIds) {
      saveHeader.BatteryDesignTypeId = formModel.BatteryDesignTypeIds.BatteryDesignTypeId;
    }
    saveHeader.Updated = this.dataProject.Updated;

    return saveHeader;
  }

  public onValueChanged(data?: any, notify: boolean = true) {
    if (notify) {
      this.ignoreProjectChanged = true;
      this.projectTab().ProjectChanged.emit(this.projectTab().Project);
      this.ignoreProjectChanged = false;
    }

    if (!this.ProjectHeaderForm) {
      return;
    }
    let isValid: boolean = false;
    this.poSvc.projectObserve('header', this.ProjectHeaderForm.valid);
    if (this.ProjectHeaderForm.valid && this.ProjectHeaderForm.dirty) {
      isValid = true;
      this.setTDV();
    } else {
      isValid = false;
      this.clearTDV();
    }
  }

  public setTDV(): void {
    let newTitle: string = 'HEADER*';
    this.tabTitle.emit(newTitle);
    let tdvState: boolean = true;
    this.tabTdv.emit(tdvState);
  }

  public clearTDV(): void {
    let newTitle: string = 'HEADER';
    this.tabTitle.emit(newTitle);
    let tdvState: boolean = false;
    this.tabTdv.emit(tdvState);
  }

  public async handleProjectStatusChange(e): Promise<void> {
    let newStatus = e;
    // if the project status is changed from Closed to Reactivated, then update project status and refresh the page
    if (newStatus && newStatus.Description === 'Reactivated') {
      await this.changeProjectStatus(newStatus.ProjectStatusId);
    }
  }

  public async handleProjectSubTypeChange(e): Promise<void> {
    this.projectTab().ProjectSubTypeId = e.ProjectSubTypeId;
    const formModel: IHeaderControls = this.ProjectHeaderForm?.value;
    this.setDefaultAfeApprovalProjectTeamList(this.projectTab()?.AssetTeamId, this.projectTab()?.ProjectTypeId, this.projectTab()?.ProjectSubTypeId, formModel?.AfeApprovalTeam?.projectName, formModel?.ExploreDevelopIds?.value);
  }

  public async handleExploreDevelopIdChange(e): Promise<void> {
    const projectTab = this.projectTab();
    this.setDefaultAfeApprovalProjectTeamList(projectTab?.AssetTeamId, projectTab?.ProjectTypeId, projectTab?.ProjectSubTypeId, null, e?.value);
  }

  private async changeProjectStatus(projectStatusId: number, persist: boolean = true) {
    this.ProcessingForm = true;
    let updateForm = {
      ProjectStatusId: projectStatusId,
      Updated: this.dataProject.Updated
    };
    if (persist) await this.projectHeaderService.update(this.projectId, updateForm as IProject);
    await this.getProjectHeaderInformation(this.projectId, this.oDataProjectQuery);
    this.projectTab().Project.ProjectStatusId = projectStatusId;
    this.ignoreProjectChanged = true;
    this.projectTab().ProjectChanged.next(this.projectTab().Project);
    this.ignoreProjectChanged = false;
    let resolver: (value: any) => void = null;
    let headerStatusLoadCompleted = new Promise<any>((resolve, reject) => {
      resolver = resolve;
    });
    if (projectStatusId == 7) {   //this condition is needed as Fix - The below logic is only for reactivating of closed projects. Currently it triggers the event with 'Reactivated' status for all projects on afe creation which throws errors.
      this.headerStatus.emit({ Status: 'Reactivated', doRefreshTabs: true, completed: resolver });
    }
    await headerStatusLoadCompleted;
    this.ProcessingForm = false; //hack
    return updateForm;
  }

  public createWellViewProject(): void {
    this.generalProjectService.createProjectWellView(this.projectId)
      .subscribe();
  }

  public projectStatusDisabled(itemArgs: { dataItem: any, index: number }) {
    return !itemArgs.dataItem.Enabled;
  }

  public projectStatusListDisabled() {
    if (this.dComboBoxProjectStatus.length == 1) return true;
    let enabled = this.dComboBoxProjectStatus.find(x => ((x) as any).Enabled);
    if (enabled) return false;
    return true;
  }

  public projectSubTypeItemDisabled(itemArgs: { dataItem: any, index: number }) {
    return !itemArgs.dataItem.IsActive;
  }

  private setPDFButtonVisibility(projectTypeId: number, projectSubTypeId: number) {
    if (projectTypeId === ProjectTypeId.PondBuild || projectTypeId === ProjectTypeId.PondBuildOther ||
      (projectTypeId === ProjectTypeId.DrillOilGasWell && projectSubTypeId === ProjectSubTypeId.DrillHorizontal) ||
      (projectTypeId === ProjectTypeId.CompleteOilGasWell && projectSubTypeId === ProjectSubTypeId.Horizontal) ||
      projectTypeId === ProjectTypeId.CompleteStimOAG || projectTypeId === ProjectTypeId.CompletePostStimOAG ||
      projectTypeId === ProjectTypeId.Flowline || projectTypeId === ProjectTypeId.WellHookup || projectTypeId === ProjectTypeId.WellPadFacility) {
      this.showPdfReportButton = true;
    }
  }

  private async processProjectHeaderInformation(payload: Project) {
    this.formLoaded = false;
    this.dataProject = payload;

    this.setPDFButtonVisibility(payload.ProjectTypeId, payload.ProjectSubTypeId);

    let projectTypeId: number = payload.ProjectTypeId;

    let disableAllEdits = payload.ProjectStatusId === ProjectStatusId.Closed ||
      payload.ProjectStatusId === ProjectStatusId.Cancelled;

    let enableDivision = payload.ProjectStatusId === ProjectStatusId.Draft ||
      payload.ProjectStatusId === ProjectStatusId.Preliminary;

    this.handleExploreDevelopIds(payload.ProjectTypeId);

    // new field ProjectEconomics - not in the ScreenBehavior table, need to handle separately for read-only.
    // this code can be expanced to handle additional new fields that are not dependant on Project Type
    if (disableAllEdits) {
      this.ProjectHeaderForm.controls.ProjectEconomics.disable();
    } else {
      this.ProjectHeaderForm.controls.ProjectEconomics.enable();
    }
    // ************************
    if (projectTypeId === ProjectTypeId.TankBattery) {
      let control = this.ProjectHeaderForm.controls.BatteryDesignTypeIds;
      control.setValidators([Validators.required]);
      control.updateValueAndValidity();
    }
    if (projectTypeId === 45 || projectTypeId === 46) {
      this.canCreateWellViewProject = true;
    }
    if (projectTypeId === 57) {
      this.constructionTypeLabel = 'Construction Sub Type';
      this.constructionTypeOrSequence = this.dataSources.ConstructionSequence.filtered;
    } else {
      this.constructionTypeOrSequence = this.dataSources.ConstructionType.filtered;
    }
    let dataChanged: boolean = false;
    await this.screenBehaviors.apply(payload.ProjectTypeId || 0, this._elRef,
      (fieldName: string, friendlyName: string, control: AbstractControl, behavior: ProjectTypeScreenBehavior): ProjectTypeScreenBehavior => {
        /*
         ******************************************************************
         //HERE IS WHERE YOU OVERRIDE THE SCREEN BEHAVIOR RULES ENFORCEMENT
         ******************************************************************
     */
        if (friendlyName === 'Project Location') {
          //
        }

        if (disableAllEdits && fieldName !== this.controlName(w => w.ProjectStatus)) {
          behavior.Readonly = true;
        } else if (!behavior.Readonly && !enableDivision && fieldName === this.controlName((w => w.Division))) {
          behavior.Readonly = true;
        }
        // replaced behavior.FieldName with just fieldName
        if (fieldName === this.controlName(w => w.ProjectWorkingInterest)) {
          this.workingInterestSettable = !behavior.Readonly;
        }

        if (behavior.AvailableValuesLIst && behavior.AvailableValuesLIst.length) {
          //should be a comma delim list of ids or strings
          //if strings, filter by the name field, else filter by the id field
          let ds = this.dataSources[fieldName] || this.dataSources[behavior.FieldName];
          if (ds) {
            if (this.screenBehaviors.filter(behavior, ds)) {
              dataChanged = true;
            }
          }
        }

        //NOTE: Developer, use this construct to ensure type safety, you can use a string compare
        //the below is the same as: if (fieldName === 'ConstructionType') ...
        // if (fieldName === this.controlName(w => w.ConstructionType)) {
        //   TODO: developer, you can override the behavior of the field here
        //   behavior.Visible = this.hasConstructionType;
        //   behavior.Required = this.hasConstructionType;
        // }
        return behavior;
      }
    );

    this.defaultProjectTeam = payload.DefaultProjectTeam && payload.DefaultProjectTeam.Description;
    this.isDefaultProjectTeamSync = payload.IsProjectTeamSync;

    let standardConfig: NameValueInt = { value: payload.DefaultStandardConfigurationId, name: '' };

    this.oProjectSubType = `${this.fProjectSubType}${projectTypeId}`;
    let pt: NameValueInt = { value: payload.DefaultProjectTeamId, name: '' };
    let all = zip(
      this.getProjectSubTypes(this.tProjectSubType, this.oProjectSubType),
      this.getMajorMilestoneVisibilities(),
      this.getScheduleTemplateName(),
      this.getLookupData(Table.StandardConfigurations, this.dataSources.StandardConfiguration, '$filter=(ProjectTypeId eq ' + payload.ProjectTypeId + '%20or%20ProjectTypeId%20eq%20null) and IsActive eq true'),
      this.getLookupData(this.tableDefaultProjectTeam, this.dataSources.DefaultProjectTeam, '$filter=AssetTeamId eq ' + payload.AssetTeamId),
      this.getProjectStatusMatrixByCurrentStatus(this.tProjectStatusMatrix, payload.ProjectStatus),
      this.getBatteryDesignTypes()
    );
    all.subscribe(r => {
      let teamResult = r[4] as NameValueInt[];
      this.BatteryDesignTypeIds = (r[6] as BatteryDesignType[]) ?? [];
      const selectedBatteryDesignType = this.BatteryDesignTypeIds.find(x => x.BatteryDesignTypeId === payload.BatteryDesignTypeId) ?? null;
      if (payload.DefaultProjectTeamId >= 1) {
        let team = teamResult.find(tr => tr.value === payload.DefaultProjectTeamId);
        if (team) {
          this.defaultProjectTeam = team.name;
        } else {
          this.defaultProjectTeam = '[Team Not Found]';
        }
      } else {
        this.defaultProjectTeam = 'Custom';
      }
      this.setDefaultAfeApprovalProjectTeamList(payload.AssetTeamId, payload.ProjectTypeId, payload.ProjectSubTypeId, payload.AfeApprovalTeam, payload.ExploreDevelopId);
      this.setProjectWBSType(payload.ProjectTypeId, payload.ProjectSubTypeId, payload.ExploreDevelopId);
      let ct: NameValueInt = { name: payload.ConstructionType && payload.ConstructionType.Description, value: payload.ConstructionTypeId };
      let thefunction: NameValueInt = { value: payload.FunctionId, name: '' };
      let division: NameValueInt = { name: '', value: payload.DivisionId };
      let expDev: NameValueInt = { name: '', value: payload.ExploreDevelopId === null ? null : payload.ExploreDevelopId };
      this.handleExploreDevelopIds(payload.ProjectTypeId);
      let wi: number;
      if (payload.WorkingInterest === null || payload.WorkingInterest === undefined || payload.WorkingInterest === 0) {
        wi = null;
      } else {
        wi = payload.WorkingInterest;
      }
      let variance: number = this.projectEacAndBudgetModel.HookUpVariance;
      if (variance < .01) {
        this.projectVariance = null;
        this.projectPctVariance = null;
      } else {
        this.projectVariance = this.projectEacAndBudgetModel.HookUpVariance;
      }
      let nv: number = null;
      let afe: number = this.projectEacAndBudgetModel.AfeBudget;
      if ((afe > 1) && (this.projectVariance > 0)) {
        this.projectPctVariance = this.projectVariance / afe * 100;
      } else {
        this.projectPctVariance = null;
      }

      let netWi: number = this.workingInterestSettable ? wi : this.totalWorkingInterest;
      this.NetValueLabel = this.baseNetValueLabel;

      if ((afe > 1) && (netWi > .00001)) {
        nv = afe * netWi;
      } else {
        if (netWi && netWi < .00001) {
          this.NetValueLabel = `${this.baseNetValueLabel}, WI must be > 0.001%`;
        }
        if (afe && afe <= 1) {
          this.NetValueLabel = `${this.baseNetValueLabel}, WI must be > 0.001% and WBS Amount >$1`;
        }
        nv = null;
      }

      let ps: IProjectStatusDto;
      if (payload.ProjectStatus) {
        ps = (payload.ProjectStatus as any) as IProjectStatusDto;
      } else if (payload.ProjectStatusId >= 0) {
        ps = this.dComboBoxProjectStatus.find(x => x.ProjectStatusId == payload.ProjectStatusId);
      }

      let patch: IHeaderControls = {
        StandardConfiguration: standardConfig,
        ConstructionType: ct,
        IsNeedByDateSync: payload.IsNeedByDateSync,
        IsScheduleSync: payload.IsScheduleSync,
        IsProjectTeamSync: payload.IsProjectTeamSync,
        Name: payload.Name,
        ProjectDescription: payload.ProjectDescription,
        ProjectEconomics: payload.ProjectEconomics,
        Location: payload.Location,
        Prospect: payload.Prospect,
        ProjectSubType: payload.ProjectSubType,
        IsNeedByDateOverride: payload.IsNeedByDateOverride,
        DefaultProjectTeam: pt,
        TypeCurveArea: payload.TeamDevelopmentAreaId,
        ProjectStatus: ps,
        Function: thefunction,
        Division: division,
        ExploreDevelopIds: expDev,
        ProjectWorkingInterest: netWi,
        NetValue: nv,
        WBSNumber: payload.AfeNumber,
        BatteryDesignTypeIds: selectedBatteryDesignType
      };
      this.ProjectHeaderForm.patchValue(patch);
      this.ProjectHeaderForm.controls.IsProjectTeamSync.disable();
      this.ProjectHeaderForm.controls.WBSNumber.disable();
      this.formLoaded = true;
    });

    if (!dataChanged) {
      this.ResetForm();
    }
  }

  private generateForm() {
    this.ProjectHeaderForm = this.fb.group(this.controlsConfig);

    let friendlyMap: { [key: string]: string } = {
      'Project Sub Type ID': this.controlName(w => w.ProjectSubType),
      'WBS Mapping Template Sync': this.controlName(w => w.IsScheduleSync),
      'Standard Configuration': this.controlName(w => w.StandardConfiguration),
      'Division': this.controlName(w => w.Division),
      'Explore / Develop Description': this.controlName(w => w.ExploreDevelopIds),
      'Project Description': this.controlName(w => w.ProjectDescription),
      'Project Economics': this.controlName(w => w.ProjectEconomics),
      'WBS Approval Team': this.controlName(w => w.AfeApprovalTeam),
      'Project Sub Type': this.controlName(w => w.ProjectSubType),
      'Project Team Sync': this.controlName(w => w.IsProjectTeamSync),
      'NetValue': this.controlName(w => w.NetValue),
      'Project Name': this.controlName(w => w.Name),
      'Project Working Interest': this.controlName(w => w.ProjectWorkingInterest),
      'Project Status': this.controlName(w => w.ProjectStatus),
      'Project Team': this.controlName(w => w.DefaultProjectTeam),
      'Prospect': this.controlName(w => w.Prospect),
      'Construction Type': this.controlName(w => w.ConstructionType),
      'Function ID': this.controlName(w => w.Function),
      'Project Location': this.controlName(w => w.Location),
      'Battery Design Type': this.controlName(w => w.BatteryDesignTypeIds),
    };
    this.screenBehaviors.setup(this._elRef, this.controlsConfig, friendlyMap,
      this.ProjectHeaderForm, this.fieldVisibility as any, null);
    this.ProjectHeaderForm.valueChanges
      .subscribe(data => this.onValueChanged(data, false));
    this.onValueChanged();
  }

  private getFormValidationErrors(form: UntypedFormGroup): NameValueString[] {
    let results: NameValueString[] = [];
    Object.keys(form.controls).forEach(key => {
      const controlErrors: ValidationErrors = form.get(key).errors;
      if (controlErrors !== null) {
        Object.keys(controlErrors).forEach(keyError => {
          let nvs: NameValueString = {
            name: key,
            value: keyError + ', err value: ' + controlErrors[keyError]
          };
          results.push(nvs);
        });
      }
    });
    return results;
  }
}

interface ProjectAsset {
  AssetId: number;
  AssetTypeId: number;
  PercentAllocation: number | null;
  ActualWorkingInterest?: number | null;
  EstimatedWorkingInterest?: number | null;
  PreliminaryWorkingInterest?: number | null;
  ContributedWorkingInterest?: number | null;
}

export interface HeaderStatusOutput {
  Status: string;
  doRefreshTabs: boolean;
  completed: (any) => void;
}

export interface IHeaderControls {
  IsNeedByDateSync: any;
  IsNeedByDateOverride: any;
  IsScheduleSync: any;
  DefaultProjectTeam: any;
  StandardConfiguration: any;
  BatteryDesignTypeIds: any;
  IsProjectTeamSync: any;
  TypeCurveArea: any;
  Division: any;
  ExploreDevelopIds: any;
  ConstructionType: any;
  Function: any;
  ProjectSubType: any;
  Name: any;
  ProjectDescription: any;
  ProjectEconomics: any;
  Location: any;
  Prospect: any;
  ProjectStatus: any;
  ProjectWorkingInterest: any;
  NetValue: any;
  AfeApprovalTeam?: any;
  WBSNumber?: any,
  WBSType?: any,
  WBSSubType?: any
}